import { assert } from '@disy/lib/custom-error';

import type { AttributeType } from 'cadenza/repository/model/attribute-type.gen';
import { isNumeric } from 'cadenza/repository/model/attribute-type.gen';

const COMMON_DATE_FORMATS = [
  'dd.MM.yyyy',
  'dd.MM.yy',
  'dd-MM-yyyy',
  'dd-MM-yy',
  'yyyy-MM-dd',
  'dd.MM.yyyy HH:mm:ss',
  'dd.MM.yyyy HH:mm',
  'yyyy',
];

const COMMON_DOUBLE_FORMATS = ['#,##0.00', '0.00', '0.####', '0.##'];

export const COMMON_FRACTION_FORMATS = ['0%', '0.0%', '0.00%'];

const COMMON_INT_FORMATS = ['#,##0', '0', '#'];

const COMMON_DURATION_FORMATS = ['Auto', 'HH:mm:ss', 'mm:ss', 'ss'];

/**
 * Returns common formats for the given attribute type.
 *
 * @param attributeType - The attribute type
 * @return Common formats
 * @see AttributeTypeCommonFormatsVisitor.java
 */
export function getCommonFormats(attributeType: AttributeType) {
  switch (attributeType) {
    case 'BIGDECIMAL':
    case 'DOUBLE':
      return COMMON_DOUBLE_FORMATS;
    case 'LONG':
    case 'INT':
      return COMMON_INT_FORMATS;
    case 'DATE':
      return COMMON_DATE_FORMATS;
    case 'DURATION':
      return COMMON_DURATION_FORMATS;
  }
  throw new Error(`Unsupported attribute type "${attributeType}"`);
}

/**
 * Returns the precision defined by the format.
 *
 * @param format - The format according to amChart docs
 * {@link https://www.amcharts.com/docs/v5/concepts/formatters/formatting-numbers/}
 * @param attributeType - The type of the attribute
 * @return Format precision
 */
export function getPrecisionFromFormat(format?: string, attributeType?: AttributeType) {
  if (format === undefined || attributeType === undefined || !isNumeric(attributeType)) {
    return;
  }
  const splitFormat = format.split('.');
  assert(splitFormat.length < 3, 'The format does not comply with amChart guidelines');
  // We want to count only the # and 0 right after the radix point
  return splitFormat[1]?.match(/^[#0]+/g)?.[0].length ?? 0;
}
